import { useRouter } from 'next/compat/router'
import { usePathname } from 'next/navigation'
import { useEffect } from 'react'
import ExtensionInstallBanner from '~/src/components/ExtensionInstallBanner'
import Layout from '~/src/components/Layout'
import LottiePlayer from '~/src/components/LottiePlayer'
import { useAppContext } from '~/src/context/AppProvider'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useShowIntercom from '~/src/hooks/useShowIntercom'
import { cn } from '~/src/lib/utils'

interface Props {
  statusCode?: number
  title?: string
  subtitle?: React.ReactNode
  /**
   * Optional block rendered under the subtitle. Used by the 404 page to offer
   * onward links — a dead end is a dead end for a crawler as much as for a
   * person, and these links are what make the 404 body useful to an agent
   * (they survive into the `Accept: text/markdown` rendering of the page).
   * Left undefined by the generic error pages, which keep their old layout.
   */
  actions?: React.ReactNode
}

export const ErrorPageContent: React.FC<Props & { className?: string }> = ({
  statusCode,
  title,
  subtitle = "Looks like we're having issues, we're sorry",
  actions,
  className,
}) => {
  const error = statusCode ? `${statusCode} Error` : title || 'Error'

  return (
    <div
      className={cn(
        'flex flex-col items-center space-y-4 px-4 text-center',
        className
      )}
    >
      <LottiePlayer
        src='https://lottie.host/b8dd671a-be70-4d9c-801c-93b54cc8fd5a/rATEr3c9ql.lottie'
        loop
        autoplay
        style={{
          height: 100,
          width: 100,
        }}
      />
      <h1 className='max-w-lg text-center text-xl font-bold'>{error}</h1>
      <div className='max-w-md text-center text-3xl font-black'>{subtitle}</div>
      {actions}
    </div>
  )
}

export const ErrorPage: React.FC<Props> = ({
  statusCode,
  title,
  subtitle = "Looks like we're having issues, we're sorry",
  actions,
}) => {
  // next/compat/router: the pages NextRouter when mounted under pages/, null
  // under app/. Under app/ the Intercom path attribute is the pathname only
  // (the query string would require useSearchParams → CSR bailout).
  const compatRouter = useRouter()
  const appPathname = usePathname()
  const asPath = compatRouter ? compatRouter.asPath : appPathname
  const error = statusCode ? `${statusCode} Error` : title || 'Error'
  const { hash } = useAppContext()
  const { authUser } = useAuthUser()
  const { tempUserId } = useGetTempUserId()

  const { enableIntercom } = useShowIntercom({
    customAttributes: {
      hash,
      tempUserId,
      userId: authUser?.uid,
      path: asPath,
      error,
    },
  })

  // Enable Intercom
  useEffect(() => {
    enableIntercom()
  }, [enableIntercom])

  return (
    <Layout
      nextSeo={{
        title: title || 'Error',
        noindex: true,
        nofollow: true,
      }}
    >
      <div className='w-full space-y-10 pt-8 dark:bg-black'>
        <ErrorPageContent
          className='mx-auto flex max-w-sm flex-col items-center space-y-4 px-4 text-center md:max-w-md'
          statusCode={statusCode}
          title={title}
          subtitle={subtitle}
          actions={actions}
        />
        <ExtensionInstallBanner appStoreCampaign='ErrorPageBottomBanner' />
      </div>
    </Layout>
  )
}

export default ErrorPage
